import { Component, ElementRef, Inject, OnInit, Signal, ViewChild } from '@angular/core';
import { AsyncPipe, formatDate, NgFor, NgIf } from '@angular/common';
import {
    AbstractControl,
    FormsModule,
    ReactiveFormsModule,
    UntypedFormBuilder,
    UntypedFormGroup,
    ValidatorFn,
    Validators
} from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { debounceTime, take } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Delegation, DelegationAddEdit } from 'src/app/delegations/models/delegation';
import {
    DelegationNoTimeSelectedDefault,
    DelegationType,
    DelegationTypeOptions
} from 'src/app/delegations/models/delegation-type.enum';
import {
    selectDelegationEmployees,
    selectDelegationTypes
} from 'src/app/delegations/store/selectors/delegations.selectors';
import { DelegationUser } from 'src/app/delegations/models/delegation-user';
import {
    clearDelegationEmployees,
    getDelegationTypes,
    searchDelegationEmployees
} from 'src/app/delegations/store/actions/delegations.actions';
import { DelegationsState } from 'src/app/delegations/store/delegations.state';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import minMax from 'dayjs/plugin/minMax';
import { NovaValidators } from 'src/app/shared/validators/nova.validators';
import { LDFeatureManager } from 'src/app/shared/services/ld-feature-manager';
import { FeatureFlag } from 'src/app/shared/models/enums/feature-flag';
import { ESPressoFeatureFlags } from 'src/app/shared/models/enums/team-feature-flags/espresso-feature-flags.enum';
import { MatIconModule } from '@angular/material/icon';
import { TimePickerModule } from '@progress/kendo-angular-dateinputs';
import { isEmpty } from 'src/app/shared/utilities/obj-utilities';

dayjs.extend(utc);
dayjs.extend(minMax);

@UntilDestroy()
@Component({
    selector: 'nova-delegation-add-edit-dialog',
    standalone: true,
    imports: [
        NgFor,
        NgIf,
        AsyncPipe,
        MatDialogModule,
        MatButtonModule,
        MatFormFieldModule,
        MatInputModule,
        MatIconModule,
        MatAutocompleteModule,
        MatListModule,
        MatDatepickerModule,
        MatNativeDateModule,
        FormsModule,
        ReactiveFormsModule,
        TimePickerModule
    ],
    templateUrl: './delegation-add-edit-dialog.component.html',
    styleUrls: ['./delegation-add-edit-dialog.component.scss']
})
export class DelegationAddEditDialogComponent implements OnInit {
    readonly delegationTypeOptions = DelegationTypeOptions;
    _selectedEmployee: DelegationUser;
    startOfDay: Date;

    delegation: Delegation;
    ownerAspNetUserId: string;
    employees$: Observable<DelegationUser[]>;
    delegationTypes$: Observable<Array<{ key: DelegationType; value: string }>>;
    delegationFormGroup: UntypedFormGroup;
    delegationTypeEnum = DelegationType;
    auth0NovaImpersonationSignal: Signal<boolean>;
    auth0NovaLinkedProfilesSignal: Signal<boolean>;
    delegationSaveButtonFixEnabled = false;
    showImpersonationWarningMessage = false;
    delegationDateValidationEnabled = false;
    novaDelegationTimeframeSignal: Signal<boolean>;
    auth0DelegateNovaImpersonationPermissionSignal: Signal<boolean>;

    get delegationEndTimeControl(): AbstractControl {
        return this.delegationFormGroup.get('delegationEndTime');
    }

    get delegationStartTimeControl(): AbstractControl {
        return this.delegationFormGroup.get('delegationStartTime');
    }

    @ViewChild('employeeSearchInput')
    employeeSearchInput: ElementRef<HTMLInputElement>;

    readonly DATE_FORMAT = 'YYYY-MM-DD';
    readonly TIME_FORMAT = 'HH:mm';

    constructor(
        @Inject(MAT_DIALOG_DATA) private readonly _data: { delegation: Delegation; ownerAspNetUserId: string },
        private readonly _store: Store<DelegationsState>,
        private readonly _formBuilder: UntypedFormBuilder,
        private readonly _matDialogRef: MatDialogRef<DelegationAddEditDialogComponent>,
        private readonly _ldFeatureManager: LDFeatureManager
    ) {
        this.auth0DelegateNovaImpersonationPermissionSignal = this._ldFeatureManager.isEnabledSignal(
            FeatureFlag.Auth0DelegateNovaImpersonationPermission
        );
        this.auth0NovaImpersonationSignal = this._ldFeatureManager.isEnabledSignal(FeatureFlag.Auth0NovaImpersonation);
        this.auth0NovaLinkedProfilesSignal = this._ldFeatureManager.isEnabledSignal(
            FeatureFlag.Auth0NovaLinkedProfiles
        );
        this.novaDelegationTimeframeSignal = this._ldFeatureManager.isEnabledSignal(
            FeatureFlag.NovaDelegationTimeframe
        );
        this.delegation = this._data.delegation;
        this.ownerAspNetUserId = this._data.ownerAspNetUserId;
    }

    ngOnInit(): void {
        this._store.dispatch(clearDelegationEmployees());
        if (this.auth0DelegateNovaImpersonationPermissionSignal()) {
            this._store
                .select(selectDelegationTypes)
                .pipe(take(1))
                .subscribe((types) => {
                    if (!types || types.length === 0) {
                        this._store.dispatch(getDelegationTypes());
                    }
                });
        }

        this._ldFeatureManager
            .isEnabled(ESPressoFeatureFlags.NovaDelegationFixSaveButton)
            .pipe(untilDestroyed(this))
            .subscribe((flag) => (this.delegationSaveButtonFixEnabled = flag));

        this._ldFeatureManager
            .isEnabled(ESPressoFeatureFlags.NovaDelegationDateValidation)
            .pipe(untilDestroyed(this))
            .subscribe((flag) => (this.delegationDateValidationEnabled = flag));

        this.buildForm();

        this.employees$ = this._store.select(selectDelegationEmployees);
        this.delegationTypes$ = this.auth0DelegateNovaImpersonationPermissionSignal()
            ? this._store.select(selectDelegationTypes)
            : of(this.delegationTypeOptions);

        this.delegationFormGroup
            .get('employeeSearch')
            .valueChanges.pipe(debounceTime(300), untilDestroyed(this))
            .subscribe((value) => {
                if (value?.fullName) {
                    this._store.dispatch(searchDelegationEmployees({ query: value?.fullName }));
                } else {
                    this._store.dispatch(searchDelegationEmployees({ query: value }));
                    // Clear selectedEmployeeId if user is typing without selecting
                    if (typeof value === 'string' && this.delegationSaveButtonFixEnabled) {
                        const selectedEmployeeControl = this.delegationFormGroup.get('selectedEmployeeId');
                        if (selectedEmployeeControl) {
                            selectedEmployeeControl.patchValue(null);
                            selectedEmployeeControl.markAsDirty();
                        }
                    }
                }
            });
    }

    startEndDateValidator: ValidatorFn = (control: AbstractControl) => {
        const startDate = control.get('delegationStartDate').value;
        const endDate = control.get('delegationEndDate').value;
        const startTime = control.get('delegationStartTime').value;
        const endTime = control.get('delegationEndTime').value;
        const endDateControl = control.get('delegationEndDate');

        if (!isEmpty(startDate) && !isEmpty(endDate) && !isEmpty(startTime) && !isEmpty(endTime)) {
            const startDateTime = dayjs(
                `${dayjs(startDate).format(this.DATE_FORMAT)}T${formatDate(startTime, this.TIME_FORMAT, 'en')}`
            );
            const endDateTime = dayjs(
                `${dayjs(endDate).format(this.DATE_FORMAT)}T${formatDate(endTime, this.TIME_FORMAT, 'en')}`
            );
            if (endDateTime.isBefore(startDateTime)) {
                const existingErrors = endDateControl.errors || {};
                endDateControl.setErrors({ ...existingErrors, startDateAfterEndDate: true });
                return { startDateAfterEndDate: true };
            }
        }

        if (endDateControl) {
            const existingErrors = endDateControl.errors || {};
            if (existingErrors.startDateAfterEndDate) {
                const { startDateAfterEndDate, ...remainingErrors } = existingErrors;
                const hasOtherErrors = Object.keys(remainingErrors).length > 0;
                endDateControl.setErrors(hasOtherErrors ? remainingErrors : null);
            }
        }
        return null;
    };

    buildForm() {
        this.startOfDay = dayjs().startOf('day').toDate();

        this.delegationFormGroup = this._formBuilder.group({
            employeeSearch: this._formBuilder.control(''),
            delegationStartDate: this._formBuilder.control(new Date(), [Validators.required]),
            delegationEndDate: this._formBuilder.control('', [NovaValidators.minDate(this.startOfDay)]),
            delegationTypes: this._formBuilder.control(null, [Validators.required]),
            selectedEmployeeId: this._formBuilder.control(null, [Validators.required])
        });

        if (this.novaDelegationTimeframeSignal()) {
            this.delegationFormGroup.addControl(
                'delegationStartTime',
                this._formBuilder.control(
                    dayjs()
                        .set('hour', DelegationNoTimeSelectedDefault.StartHour)
                        .set('minute', DelegationNoTimeSelectedDefault.StartMinute)
                        .toDate()
                )
            );
            this.delegationFormGroup.addControl(
                'delegationEndTime',
                this._formBuilder.control({ value: null, disabled: true })
            );
            this.delegationFormGroup.addValidators(this.startEndDateValidator);

            this.delegationFormGroup
                .get('delegationEndDate')
                .valueChanges.pipe(untilDestroyed(this))
                .subscribe((change) => {
                    if (change) {
                        this.delegationEndTimeControl.enable();
                        if (this.delegationEndTimeControl.value == null) {
                            this.delegationEndTimeControl.setValue(
                                dayjs()
                                    .set('hour', DelegationNoTimeSelectedDefault.EndHour)
                                    .set('minute', DelegationNoTimeSelectedDefault.EndMinute)
                                    .toDate()
                            );
                        }
                    } else {
                        this.delegationEndTimeControl.disable();
                        this.delegationEndTimeControl.setValue(null);
                    }
                    this.delegationFormGroup.markAllAsTouched();
                    this.delegationEndTimeControl.updateValueAndValidity();
                });
        }

        if (this.delegation) {
            const startDate = dayjs(`${this.delegation.delegationStartDate}Z`).toDate();
            const endDate = this.delegation.delegationEndDate
                ? dayjs(`${this.delegation.delegationEndDate}Z`).toDate()
                : null;

            const isStartDateTimeSet =
                startDate.getHours() !== DelegationNoTimeSelectedDefault.StartHour ||
                startDate.getMinutes() !== DelegationNoTimeSelectedDefault.StartMinute;
            const isEndDateTimeSet =
                endDate &&
                (endDate.getHours() !== DelegationNoTimeSelectedDefault.EndHour ||
                    endDate.getMinutes() !== DelegationNoTimeSelectedDefault.EndMinute);

            this.delegationFormGroup.patchValue(this.delegation);
            this.delegationFormGroup.controls.delegationStartDate.patchValue(startDate);

            if (this.novaDelegationTimeframeSignal() && isStartDateTimeSet) {
                this.delegationFormGroup.controls.delegationStartTime.patchValue(startDate);
            }

            if (this.delegation.delegationEndDate) {
                this.delegationFormGroup.controls.delegationEndDate.patchValue(endDate);
                if (this.novaDelegationTimeframeSignal() && isEndDateTimeSet) {
                    this.delegationFormGroup.controls.delegationEndTime.patchValue(endDate);
                }
            }
            this.delegationFormGroup.controls.delegationTypes.patchValue(
                this.delegation.delegationTypes.map((t) => t.key)
            );
            this.delegationFormGroup.controls.selectedEmployeeId.patchValue(this.delegation.delegatedUser.aspNetUserId);

            this.showImpersonationWarningMessage =
                this.delegationFormGroup.controls.delegationTypes?.value.includes(DelegationType.InAppImpersonation) &&
                this.auth0NovaImpersonationSignal();
        }
    }

    get titleCaption(): string {
        return this.delegation ? 'Edit coverage' : 'Set up coverage';
    }

    get saveCaption(): string {
        return this.delegation ? 'Save' : 'Set up coverage';
    }

    saveDelegation(): void {
        if (this.delegationFormGroup.valid) {
            const formValue = this.delegationFormGroup.value;
            const currentTime = dayjs().format(`T${this.TIME_FORMAT}:ss`);
            const startDate = dayjs(formValue.delegationStartDate).format(this.DATE_FORMAT);
            let endDateUtc: Date;
            let startDateUtc: Date;

            if (this.novaDelegationTimeframeSignal()) {
                const startTime = formValue.delegationStartTime
                    ? formatDate(formValue.delegationStartTime, this.TIME_FORMAT, 'en')
                    : `${DelegationNoTimeSelectedDefault.StartHour.toString().padStart(2, '0')}:${DelegationNoTimeSelectedDefault.StartMinute.toString().padStart(2, '0')}`;

                const endTime = formValue.delegationEndTime
                    ? formatDate(formValue.delegationEndTime, this.TIME_FORMAT, 'en')
                    : `${DelegationNoTimeSelectedDefault.EndHour}:${DelegationNoTimeSelectedDefault.EndMinute}`;

                startDateUtc = dayjs(`${startDate}T${startTime}`).toDate();

                if (formValue.delegationEndDate) {
                    const endDate = dayjs(formValue.delegationEndDate).format(this.DATE_FORMAT);
                    endDateUtc = dayjs(`${endDate}T${endTime}`).toDate();
                }
            } else {
                startDateUtc = dayjs(`${startDate}${currentTime}`).toDate();
                if (formValue.delegationEndDate) {
                    const endDate = dayjs(formValue.delegationEndDate).format(this.DATE_FORMAT);
                    endDateUtc = dayjs(`${endDate}${currentTime}`).toDate();
                }
            }

            const result: DelegationAddEdit = {
                id: this.delegation?.id,
                delegatedAspNetUserId: formValue.selectedEmployeeId,
                delegationStartDate: startDateUtc,
                delegationEndDate: endDateUtc,
                ownerAspNetUserId: this._data.ownerAspNetUserId,
                delegationTypeIds: formValue.delegationTypes
            };
            this._matDialogRef.close(result);
        }
    }

    employeeDisplay(employee: DelegationUser): string {
        return employee?.fullName;
    }

    employeeSelected(option: MatAutocompleteSelectedEvent): void {
        this._selectedEmployee = option.option.value;
        this.delegationFormGroup.get('selectedEmployeeId').patchValue(this._selectedEmployee.aspNetUserId ?? null);
    }

    getStartMinDateValue(): Date {
        return this.delegation ? this.delegation.delegationStartDate : this.startOfDay;
    }

    getEndMinDateValue(): Date | undefined {
        return dayjs
            .max(dayjs(this.delegationFormGroup.get('delegationStartDate').value), dayjs(this.startOfDay))
            .toDate();
    }

    selectionChange(event: any): void {
        this.showImpersonationWarningMessage =
            event.source._value.includes(DelegationType.InAppImpersonation) && this.auth0NovaImpersonationSignal();
    }
}
